/**
 * Analytics Portal V1 — shared KPI card (Phase 9, plan §12.2).
 *
 * The card is a pure renderer of one **metric object** from the API
 * (`{value, available, partial, reason, notice, change, change_pct, is_new}`):
 *   - `available: true`  → the real value (a zero from the API is a real zero and is shown).
 *   - `available: false` → `MetricUnavailable`; never a zero, dash or substituted number.
 * An unsupported metric never reaches this component — the API omits it, so no card mounts.
 */
import React from "react";
import { Card } from "@/components/ui/card";
import { ComparisonChip } from "./ComparisonChip";
import { MetricUnavailable } from "./MetricUnavailable";

const format = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value !== "number") return String(value);
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

export const KpiCard = ({ metricKey, label, metric, suffix = "", hint,
                          rangeSuggestion, onUseSupportedRange }) => {
  if (!metric) return null;
  const unavailable = metric.available === false;
  return (
    <Card className="p-4" data-testid={`analytics-kpi-${metricKey}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {!unavailable && <ComparisonChip metricKey={metricKey} metric={metric} />}
      </div>
      {unavailable ? (
        <MetricUnavailable metricKey={metricKey} metric={metric}
                           rangeSuggestion={rangeSuggestion}
                           onUseSupportedRange={onUseSupportedRange} />
      ) : (
        <div className="mt-1 text-2xl font-[680] tracking-tight text-foreground"
             data-testid={`analytics-kpi-value-${metricKey}`}>
          {format(metric.value)}{suffix}
        </div>
      )}
      {hint && !unavailable && (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      )}
      {!unavailable && metric.forward_only && metric.tracked_since && (
        <p className="mt-1 text-xs text-muted-foreground"
           data-testid={`analytics-kpi-forward-only-${metricKey}`}>
          Measured from {metric.tracked_since}
        </p>
      )}
    </Card>
  );
};
